// modules/ad-assets/AdAccountsTab.tsx
// Таблиця рекламних кабінетів з розширеним функціоналом:
// - Сортування по статусу (клік на хедер)
// - Фільтр по статусу
// - Колонка "Піксель" (з копіюванням ID)
// - Копіювання ID при кліку
// - Кнопка ручного оновлення кабінетів

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAssets } from './useAssets.ts';
import type { AdAccount } from '../../core/types.ts';

interface Props {
  socialAccountId: string;
  accountName?: string | null;
}

type SortKey = 'accountStatus' | 'name' | null;
type SortDir = 'asc' | 'desc';

function getStatusLabel(accountStatus: number | null): { text: string; cls: string } {
  if (accountStatus === null) return { text: 'Невідомо', cls: 'badge--expired' };
  switch (accountStatus) {
    case 1:   return { text: 'Активний', cls: 'badge--active' };
    case 2:   return { text: 'Вимкнений', cls: 'badge--disconnected' };
    case 3:   return { text: 'Не розрахований', cls: 'badge--expired' };
    case 7:   return { text: 'Перевірка ризиків', cls: 'badge--expired' };
    case 9:   return { text: 'Пільговий період', cls: 'badge--expired' };
    case 101: return { text: 'Закритий', cls: 'badge--disconnected' };
    default:  return { text: `Код ${accountStatus}`, cls: 'badge--expired' };
  }
}

function formatTimezone(tzName: string | null): string {
  if (!tzName) return '—';
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tzName,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (offsetPart) {
      const offset = offsetPart.replace('GMT', '') || '+0';
      return `${tzName}(${offset})`;
    }
  } catch {
    // fallback
  }
  return tzName;
}

const STATUS_FILTER_OPTIONS = [
  { value: 'all',    label: 'Всі' },
  { value: '1',     label: 'Активний' },
  { value: '2',     label: 'Вимкнений' },
  { value: '101',   label: 'Закритий' },
  { value: 'other', label: 'Інші' },
];

export function AdAccountsTab({ socialAccountId, accountName }: Props) {
  const {
    adAccounts,
    adAccountsLoading,
    adAccountsError,
    fetchAdAccounts,
    refreshAdAccounts,
    adAccountsRefreshing,
  } = useAssets();

  // — sorting
  const [sortKey, setSortKey] = useState<SortKey>('accountStatus');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  // — filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // — copy tooltip
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // — refresh toast
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);

  useEffect(() => {
    if (socialAccountId) fetchAdAccounts(socialAccountId);
  }, [socialAccountId, fetchAdAccounts]);

  const handleCopyId = useCallback(async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch {
      // fallback
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    await refreshAdAccounts(socialAccountId);
    setRefreshMsg('Кабінети оновлено!');
    setTimeout(() => setRefreshMsg(null), 2500);
  }, [refreshAdAccounts, socialAccountId]);

  const handleSortByStatus = useCallback(() => {
    if (sortKey === 'accountStatus') {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey('accountStatus');
      setSortDir('asc');
    }
  }, [sortKey]);

  const handleSortByName = useCallback(() => {
    if (sortKey === 'name') {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey('name');
      setSortDir('asc');
    }
  }, [sortKey]);

  const filteredAndSorted = useMemo(() => {
    let list = [...adAccounts];

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'other') {
        list = list.filter(
          (a) => ![1, 2, 101].includes(a.accountStatus ?? -1)
        );
      } else {
        const code = parseInt(statusFilter, 10);
        list = list.filter((a) => a.accountStatus === code);
      }
    }

    // Sort
    if (sortKey) {
      list.sort((a, b) => {
        let av: number | string | null;
        let bv: number | string | null;
        if (sortKey === 'accountStatus') {
          av = a.accountStatus ?? 999;
          bv = b.accountStatus ?? 999;
        } else {
          av = a.name ?? '';
          bv = b.name ?? '';
        }
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return list;
  }, [adAccounts, statusFilter, sortKey, sortDir]);

  const sortIcon = (key: SortKey) => {
    if (sortKey !== key) return <span className="sort-icon sort-icon--neutral">↕</span>;
    return (
      <span className="sort-icon sort-icon--active">
        {sortDir === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <div className="page-body" style={{ padding: 0 }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
          <h2 style={{ fontSize: '1rem', margin: 0 }}>
            Рекламні кабінети
            {adAccounts.length > 0 && (
              <span className="text-subtle font-mono text-sm" style={{ marginLeft: 'var(--space-2)' }}>
                ({filteredAndSorted.length}/{adAccounts.length})
              </span>
            )}
          </h2>

          {/* Refresh button */}
          <button
            className="btn btn--secondary btn--sm"
            onClick={handleRefresh}
            disabled={adAccountsRefreshing || adAccountsLoading}
            title="Оновити кабінети з Facebook (тільки кабінети, без сторінок і постів)"
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', marginLeft: 'auto' }}
          >
            <span
              style={{
                display: 'inline-block',
                fontSize: '0.9rem',
                animation: adAccountsRefreshing ? 'spin 1s linear infinite' : 'none',
              }}
            >
              ↻
            </span>
            {adAccountsRefreshing ? 'Оновлення…' : 'Оновити кабінети'}
          </button>
        </div>

        {accountName && (
          <p className="text-muted text-sm" style={{ margin: 0 }}>
            Акаунт: <span className="text-subtle">{accountName}</span>
          </p>
        )}
      </div>

      {/* Refresh success toast */}
      {refreshMsg && (
        <div className="notification notification--success" style={{ marginBottom: 'var(--space-3)' }}>
          {refreshMsg}
        </div>
      )}

      {/* Filters row */}
      {adAccounts.length > 0 && (
        <div className="ad-accounts-filters" style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-4)', alignItems: 'center' }}>
          {/* Status filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span className="text-muted text-sm">Статус:</span>
            <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`filter-chip ${statusFilter === opt.value ? 'filter-chip--active' : ''}`}
                  onClick={() => setStatusFilter(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {adAccountsLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton" style={{ height: 52, borderRadius: 'var(--radius-md)' }} />
          ))}
        </div>
      )}

      {/* Error */}
      {adAccountsError && !adAccountsLoading && (
        <div className="notification notification--error">{adAccountsError}</div>
      )}

      {/* Empty state */}
      {!adAccountsLoading && !adAccountsError && adAccounts.length === 0 && (
        <div className="glass-card empty-state">
          <div className="empty-state__title">Немає рекламних кабінетів</div>
          <div className="empty-state__desc">
            Синхронізуй акаунт, щоб завантажити кабінети з Facebook.
          </div>
        </div>
      )}

      {/* Filtered empty */}
      {!adAccountsLoading && adAccounts.length > 0 && filteredAndSorted.length === 0 && (
        <div className="glass-card empty-state" style={{ padding: 'var(--space-6)' }}>
          <div className="empty-state__title">Нічого не знайдено</div>
          <div className="empty-state__desc">Спробуй змінити фільтри.</div>
        </div>
      )}

      {/* Table */}
      {!adAccountsLoading && filteredAndSorted.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={handleSortByName}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    Назва {sortIcon('name')}
                  </span>
                </th>
                <th>ID Кабінету</th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={handleSortByStatus}
                  title="Клікни для сортування по статусу"
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    Статус {sortIcon('accountStatus')}
                  </span>
                </th>
                <th>Піксель ID</th>
                <th>Білінг</th>
                <th>Валюта</th>
                <th>Часовий пояс</th>
                <th>Оновлено</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.map((acc: AdAccount) => {
                const statusInfo = getStatusLabel(acc.accountStatus);
                const cleanId = acc.id.replace(/^act_/, '');
                const isCopied = copiedId === cleanId;
                const isPixelCopied = Boolean(acc.pixelId && copiedId === acc.pixelId);
                const updatedDate = acc.updatedAt
                  ? new Intl.DateTimeFormat('uk-UA', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }).format(new Date(acc.updatedAt))
                  : '—';
                return (
                  <tr key={acc.id}>
                    <td className="font-medium">{acc.name ?? '—'}</td>
                    <td>
                      <span
                        className={`font-mono text-sm copy-id ${isCopied ? 'copy-id--copied' : 'copy-id--default'}`}
                        onClick={() => handleCopyId(cleanId)}
                        title={isCopied ? 'Скопійовано!' : 'Клікни, щоб скопіювати ID'}
                      >
                        {cleanId}
                        <span className="copy-id__icon">
                          {isCopied ? '✓' : '⎘'}
                        </span>
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${statusInfo.cls}`}>{statusInfo.text}</span>
                    </td>
                    <td>
                      {acc.pixelIds && acc.pixelIds.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {acc.pixelIds.map((pixId) => {
                            const isPixCopied = copiedId === pixId;
                            return (
                              <span
                                key={pixId}
                                className={`font-mono text-sm copy-id ${isPixCopied ? 'copy-id--copied' : 'copy-id--default'}`}
                                onClick={() => handleCopyId(pixId)}
                                title={isPixCopied ? 'Скопійовано!' : 'Клікни, щоб скопіювати ID пікселя'}
                              >
                                {pixId}
                                <span className="copy-id__icon">
                                  {isPixCopied ? '✓' : '⎘'}
                                </span>
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-subtle">—</span>
                      )}
                    </td>
                    <td className="text-sm">
                      {acc.billingCard ? (
                        <span className="font-mono text-xs">{acc.billingCard}</span>
                      ) : (
                        <span className="text-subtle">—</span>
                      )}
                    </td>
                    <td>
                      <span className="badge badge--currency">{acc.currency ?? '—'}</span>
                    </td>
                    <td className="text-sm text-muted">{formatTimezone(acc.timezoneName)}</td>
                    <td className="text-sm text-muted">{updatedDate}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
